import { Link } from 'react-router-dom';
import { Bot, ListTodo, MessageSquare, Bell, CheckSquare } from 'lucide-react';

type Variant = 'dashboard' | 'tasks' | 'chat' | 'notifications' | 'approvals';

interface Props {
  variant: Variant;
}

const configs: Record<Variant, {
  icon: React.ReactNode;
  title: string;
  description: string;
  steps?: { label: string; href?: string }[];
}> = {
  dashboard: {
    icon: <Bot className="w-12 h-12 text-gray-300" />,
    title: 'Get started with AI Dropshipping',
    description: 'Set up your autonomous fulfillment pipeline in 4 steps:',
    steps: [
      { label: 'Add a supplier', href: '/admin/suppliers' },
      { label: 'Map products to supplier SKUs' },
      { label: 'Enable the pipeline in Config', href: '/admin/agent/config' },
      { label: 'Your first order will be auto-fulfilled' },
    ],
  },
  tasks: {
    icon: <ListTodo className="w-12 h-12 text-gray-300" />,
    title: 'No tasks yet',
    description: 'Tasks are created automatically when orders arrive, the pipeline runs, or you trigger actions from the dashboard.',
    steps: [
      { label: 'Go to the Dashboard to trigger your first action', href: '/admin/agent' },
    ],
  },
  chat: {
    icon: <MessageSquare className="w-12 h-12 text-gray-300" />,
    title: 'Chat with your AI agent',
    description: 'Ask questions about inventory, troubleshoot failed orders, or review pricing — all in natural language.',
  },
  notifications: {
    icon: <Bell className="w-12 h-12 text-gray-300" />,
    title: 'No notifications yet',
    description: "You'll see alerts here when the agent needs your attention — escalations, order failures, and price spikes.",
  },
  approvals: {
    icon: <CheckSquare className="w-12 h-12 text-gray-300" />,
    title: 'No pending approvals',
    description: 'When the agent proposes price changes or new product listings, they\'ll appear here for your review.',
  },
};

export default function AgentEmptyState({ variant }: Props) {
  const config = configs[variant];

  return (
    <div className="text-center py-12 px-4 max-w-md mx-auto" data-testid={`empty-state-${variant}`}>
      <div className="mx-auto mb-4">{config.icon}</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{config.title}</h3>
      <p className="text-sm text-gray-500 mb-4">{config.description}</p>
      {config.steps && (
        <ol className="text-left space-y-2 inline-block">
          {config.steps.map((step, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs font-bold shrink-0 mt-0.5">
                {i + 1}
              </span>
              {step.href ? (
                <Link to={step.href} className="text-sm text-blue-600 hover:text-blue-800">
                  {step.label}
                </Link>
              ) : (
                <span className="text-sm text-gray-700">{step.label}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
